import { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Renders a PDF to canvases and gives the caller a coordinate space to draw on.
//
// This is the piece the whole field model rests on. An <iframe> shows a PDF but
// tells you nothing about where anything is -- you cannot know where page 2
// starts, let alone place a signature box on it. Rendering each page to a
// canvas of known size turns the document into something addressable.
//
// Shared deliberately between the placement editor and the signing page. If
// they rendered independently, a box placed at 62% in one could land somewhere
// else in the other, and the failure would be a signature in the wrong place on
// an executed agreement.
//
// COORDINATES ARE ALWAYS FRACTIONS of page width/height, never pixels. A pixel
// offset is meaningless at a different zoom, DPI, or container width, and this
// component is used at several of each.

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

/**
 * @param url            signed URL of the PDF
 * @param renderOverlay  (pageNumber, {width,height}) => ReactNode, positioned
 *                       absolutely over that page
 * @param onPageClick    (pageNumber, {x,y}) => void, x/y as fractions
 * @param maxWidth       cap on rendered page width in CSS pixels
 */
export default function ContractPdf({
  url, renderOverlay, onPageClick, maxWidth = 820, C, onLoaded, onError,
  allowZoom = true, fullFooter, zoom: zoomProp, onZoomChange,
}) {
  const [pages, setPages] = useState([]);       // [{ pageNumber, width, height }]
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  // Zoom is controlled when the caller passes it, uncontrolled otherwise. The
  // signing page drives it to zoom in on whichever field is being filled; the
  // placement editor has no opinion and lets this component keep its own.
  const [zoomState, setZoomState] = useState(1);
  const zoom = zoomProp ?? zoomState;
  const [full, setFull] = useState(false);
  const canvasRefs = useRef({});
  const docRef = useRef(null);

  // Callback props are held in refs and kept OUT of the effect deps below.
  //
  // This is not a micro-optimisation. A parent that passes an inline arrow --
  // onError={(m) => setError(m)} -- hands over a new function on every render,
  // so listing it as a dependency re-runs the load effect on every parent state
  // change. That tore the whole document down and rebuilt it on each keystroke:
  // focus lost after one character, scroll thrown back to the top, and the page
  // jumping while someone was trying to fill it in.
  const onLoadedRef = useRef(onLoaded);
  const onErrorRef = useRef(onError);
  useEffect(() => { onLoadedRef.current = onLoaded; onErrorRef.current = onError; });

  useEffect(() => {
    if (!url) return undefined;
    let canceled = false;
    setStatus('loading');

    (async () => {
      try {
        const task = pdfjsLib.getDocument({ url });
        const doc = await task.promise;
        if (canceled) { doc.destroy(); return; }
        docRef.current = doc;

        const meta = [];
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          // scale 1 gives CSS-pixel dimensions at 72dpi, which is the space
          // percentages are relative to.
          const vp = page.getViewport({ scale: 1 });
          meta.push({ pageNumber: n, width: vp.width, height: vp.height });
        }
        if (canceled) return;
        setPages(meta);
        setStatus('ready');
        onLoadedRef.current?.(doc.numPages);
      } catch (e) {
        if (canceled) return;
        setStatus('error');
        setMessage(e?.message || 'Could not open this document.');
        onErrorRef.current?.(e?.message || 'Could not open this document.');
      }
    })();

    return () => {
      canceled = true;
      try { docRef.current?.destroy(); } catch (_) { /* already gone */ }
      docRef.current = null;
    };
    // ONLY url. Adding the callbacks here is what caused the remount-per-
    // keystroke bug described above.
  }, [url]);

  // Paint each page once its canvas exists and we know the target width.
  useEffect(() => {
    if (status !== 'ready' || !docRef.current) return undefined;
    let canceled = false;
    const tasks = [];

    (async () => {
      for (const p of pages) {
        const canvas = canvasRefs.current[p.pageNumber];
        if (!canvas || canceled) continue;
        const page = await docRef.current.getPage(p.pageNumber);
        if (canceled) return;

        const cssWidth = Math.min(maxWidth, p.width) * zoom;
        const scale = cssWidth / p.width;
        // Render at device resolution so text stays sharp, but keep the CSS
        // box at the logical size -- percentages are relative to the CSS box.
        const dpr = window.devicePixelRatio || 1;
        const vp = page.getViewport({ scale: scale * dpr });

        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = `${Math.floor(cssWidth)}px`;
        canvas.style.height = `${Math.floor(cssWidth * (p.height / p.width))}px`;

        const task = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
        tasks.push(task);
        try { await task.promise; } catch (_) { /* canceled render */ }
      }
    })();

    return () => {
      canceled = true;
      tasks.forEach((t) => { try { t.cancel(); } catch (_) {} });
    };
    // Deliberately NOT depending on `full`. Full screen keeps the same DOM (see
    // the single return below), so there is nothing to repaint -- and repainting
    // would restart a render on a page whose previous render is still being
    // canceled, which pdf.js rejects and this catch would swallow into a blank
    // page. That is exactly the bug that made page 2 come back empty.
  }, [status, pages, maxWidth, zoom]);

  const setZoom = useCallback((next) => {
    const raw = typeof next === 'function' ? next(zoom) : next;
    const clamped = Math.min(3, Math.max(0.5, Number(raw.toFixed(2))));
    if (onZoomChange) onZoomChange(clamped);
    else setZoomState(clamped);
  }, [zoom, onZoomChange]);

  const handleClick = useCallback((e, pageNumber) => {
    if (!onPageClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onPageClick(pageNumber, {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  }, [onPageClick]);

  if (status === 'loading') {
    return <div style={{ padding: '40px', textAlign: 'center', color: C?.muted, fontSize: '13px' }}>
      Rendering document…
    </div>;
  }
  if (status === 'error') {
    return <div style={{ padding: '20px', borderRadius: '8px', fontSize: '13px',
                         background: 'rgba(248,113,113,0.08)', color: C?.danger || '#f87171',
                         border: '1px solid rgba(248,113,113,0.3)' }}>
      {message}
    </div>;
  }

  const ctrl = {
    padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
    cursor: 'pointer', border: `1px solid ${C?.border || '#28395C'}`,
    background: 'transparent', color: C?.text || '#e2e8f0',
  };

  const stack = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px' }}>
      {pages.map((p) => {
        const cssWidth = Math.min(maxWidth, p.width) * zoom;
        const cssHeight = cssWidth * (p.height / p.width);
        return (
          <div key={p.pageNumber}
               style={{ position: 'relative', width: `${Math.floor(cssWidth)}px`,
                        height: `${Math.floor(cssHeight)}px`, flexShrink: 0,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.35)', background: '#fff' }}
               onClick={(e) => handleClick(e, p.pageNumber)}>
            <canvas ref={(el) => { canvasRefs.current[p.pageNumber] = el; }}
                    style={{ display: 'block' }} />
            {/* Overlay shares the page's exact box, so a child at left:62%
                sits at 62% of the page regardless of render scale. */}
            <div style={{ position: 'absolute', inset: 0 }}>
              {renderOverlay?.(p.pageNumber, { width: cssWidth, height: cssHeight })}
            </div>
            <div style={{ position: 'absolute', bottom: '-16px', right: 0,
                          fontSize: '10px', color: C?.muted || '#7d8ea2' }}>
              Page {p.pageNumber} of {pages.length}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (!allowZoom) return stack;

  const bar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
                  marginBottom: '10px', justifyContent: 'center' }}>
      <button style={ctrl} onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              disabled={zoom <= 0.5}>−</button>
      <span style={{ fontSize: '12px', color: C?.muted || '#7d8ea2', minWidth: '52px',
                     textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
      <button style={ctrl} onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
              disabled={zoom >= 3}>+</button>
      <button style={ctrl} onClick={() => setZoom(1)}>Reset</button>
      <button style={{ ...ctrl, borderColor: C?.accent || '#00A0C8', color: C?.accent || '#00A0C8' }}
              onClick={() => setFull((v) => !v)}>
        {full ? 'Exit full screen' : 'Full screen'}
      </button>
    </div>
  );

  // ONE tree for both modes, differing only in style.
  //
  // Returning a different structure for full screen tore the canvases out of the
  // DOM and rebuilt them, and a canvas that has been rebuilt is blank until
  // something repaints it. Repainting turned out to be the wrong fix: restarting
  // a render on a page whose previous render is still cancelling is rejected by
  // pdf.js, so the second page stayed empty. Keeping the same elements and only
  // changing their styles means nothing is ever torn down, so there is nothing
  // to repaint and no race to lose.
  return (
    <div style={full ? { position: 'fixed', inset: 0, zIndex: 9900, background: C?.bg || '#0A1526',
                         display: 'flex', flexDirection: 'column' } : undefined}>
      <div style={full ? { padding: '10px 14px', borderBottom: `1px solid ${C?.border || '#28395C'}` }
                       : undefined}>
        {bar}
      </div>
      <div style={full ? { flex: 1, minHeight: 0, overflow: 'auto', padding: '18px' } : undefined}>
        {stack}
      </div>
      {/* Full screen used to be a dead end: you could fill every field on the
          document and the only way onward was to hunt for "Exit full screen".
          The caller supplies whatever "and now what" means for it. */}
      {full && fullFooter
        ? <div style={{ padding: '10px 14px', borderTop: `1px solid ${C?.border || '#28395C'}`,
                        background: C?.surface || '#16233F' }}>
            {fullFooter({ exitFull: () => setFull(false) })}
          </div>
        : null}
    </div>
  );
}
